/**
 * @license Siftrock
 * (c) 2014-2018 Siftrock, Inc. http://siftrock.com
 * Unauthorized copying of this file, via any medium is strictly prohibited
 */

import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';

import { BsModalService } from 'ngx-bootstrap/modal';

import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';

import { PartnerService } from '../../services/partner.service';

import { Constants, Partner } from '../constants';
import { PartnerList, PartnerField, PartnerStaticFieldMap, PartnerSiftrockFieldMap } from '../models';
import { PartnerPickerOptions } from '../partner-picker/partner-picker.component';

@Component({
  selector: 'partner-map',
  templateUrl: './partner-map.component.html'
})
export class PartnerMapComponent implements OnInit {

  @Input()
  options: PartnerMapOptions;

  @Input()
  selectedPartner: Partner;

  @Input()
  selectedList: PartnerList;

  @Input()
  staticFieldMap: Array<PartnerStaticFieldMap>;

  @Input()
  siftrockFieldMap: Array<PartnerSiftrockFieldMap>;

  @Input()
  senderFieldMap:Array<PartnerField>;

  @Output('onPartnerChange')
  onPartnerChange: EventEmitter<Partner> = new EventEmitter<Partner>();

  @Output('onListChange')
  onListChange: EventEmitter<PartnerList> = new EventEmitter<PartnerList>();

  @Output('onStaticFieldChange')
  onStaticFieldChange: EventEmitter<Array<PartnerStaticFieldMap>> = new EventEmitter<Array<PartnerStaticFieldMap>>();

  @Output('onSiftrockFieldChange')
  onSiftrockFieldChange: EventEmitter<Array<PartnerSiftrockFieldMap>> = new EventEmitter<Array<PartnerSiftrockFieldMap>>();

  @Output('onSenderFieldChange')
  onSenderFieldChange:EventEmitter<Array<PartnerField>>  = new EventEmitter<Array<PartnerField>>();

  // Partner control
  partnerOptions: PartnerPickerOptions = {
    selectDefaultWhenOne: true
  };

  // State management
  loadingFields: boolean = false;
  partnerFields: Array<PartnerField>;
  usedFields: Array<PartnerField>;
  previousPartner: Partner; // so we can revert

  constructor(private constants: Constants,
              private partnerService: PartnerService,
              private modalService: BsModalService) { }

  ngOnInit() {
    this.loadFields();
    //this.previousPartner = JSON.parse(JSON.stringify(this.selectedPartner));
  }

  // changePartner2(partner: Partner) {
  //   this.verifyChangePartner()
  //   .then(canChange => {
  //     this.selectedPartner = partner; // need to change immediately in case we need to revert (to fire the change event)
  //     if (canChange) {
  //       this.previousPartner = JSON.parse(JSON.stringify(this.selectedPartner));
  //       this.selectedList = null;
  //       this.staticFieldMap = [];
  //       this.siftrockFieldMap = [];
  //       this.usedFields = [];
  //       this.onPartnerChange.emit(this.selectedPartner);
  //       this.onListChange.emit(this.selectedList);
  //       this.onStaticFieldChange.emit(this.staticFieldMap);
  //     }
  //     else {
  //       // revert dropdown value
  //       this.selectedPartner = JSON.parse(JSON.stringify(this.previousPartner));
  //     }
  //    });
  // }

  changePartner(partner: Partner) {
    this.selectedPartner = partner; // need to change immediately in case we need to revert (to fire the change event)
    //this.previousPartner = JSON.parse(JSON.stringify(this.selectedPartner));
    this.selectedList = null;
    this.staticFieldMap = [];
    this.siftrockFieldMap = [];
    this.usedFields = [];
    this.onPartnerChange.emit(this.selectedPartner);
    this.onListChange.emit(this.selectedList);
    this.onStaticFieldChange.emit(this.staticFieldMap);
    this.loadFields(true);
  }

  changeList(list: PartnerList) {
    this.selectedList = list;
    this.onListChange.emit(list);
  }

  changeStaticField(fields: Array<PartnerStaticFieldMap>) {
    this.staticFieldMap = fields;
    this.setUsedFields();
    this.onStaticFieldChange.emit(fields);
  }

  changeSiftrockField(fields: Array<PartnerSiftrockFieldMap>) {
    this.siftrockFieldMap = fields;
    this.setUsedFields();
    this.onSiftrockFieldChange.emit(fields);
  }

  changeSenderField(fields: Array<PartnerField>) {
    this.senderFieldMap = fields;
    this.setUsedFields();
    this.onSenderFieldChange.emit(fields);
  }

  refreshFields() {
    this.loadFields(true);
  }

  private loadFields(refreshFromPartner: boolean = false) {

    this.partnerFields = [];

    if (this.selectedPartner) {
      this.loadingFields = true;

      if (refreshFromPartner) {
        this.partnerService.clearFieldCache(this.selectedPartner.name);
      }

      this.partnerService.getFields(this.selectedPartner.name).subscribe(
        (result: any) => {
          result.forEach(field => {
            this.partnerFields.push({
              id: field.id,
              name: field.name,
              display: field.description,
              type: field.type
            });
          });

          this.partnerFields.sort((a, b) => {
            if (a.name < b.name) {
              return -1;
            }
            else {
              return 1;
            }
          });
          this.setUsedFields();
          this.loadingFields = false;
        },
        error => {
          this.loadingFields = false;
        }
      );
    }
  }

  /**
   * Keep track of used fields so they can't be mapped multiple times
   */
  private setUsedFields() {
    this.usedFields = [];
    this.usedFields = this.usedFields.concat(this.staticFieldMap.map(sf => { return sf.partnerField}));
    this.usedFields = this.usedFields.concat(this.siftrockFieldMap.map(sf => { return sf.partnerField}));
    this.usedFields = this.usedFields.concat(this.senderFieldMap);

    // add core fields to the used list since they should not be allowed to be mapped
    this.constants.coreFields.forEach(cf => {
      if (cf.partner == this.selectedPartner.name) {
        let pf = this.partnerFields.find(f => { return f.name == cf.name });
        this.usedFields.push(pf);
      }
    });
  }

  private verifyChangePartner(): Promise<boolean> {
    let needDialog: boolean = false;

    if (this.selectedList && this.selectedList.id) {
      needDialog = true;
    }

    if (this.usedFields.length > 0) {
      needDialog = true;
    }

    if (needDialog) {
      return new Promise((resolve, reject) => {
        this.modalService.show(ConfirmDialogComponent, Object.assign({}, {
          initialState: {
            message: `Changing the partner for this track task will remove current list selection and/or field mapping. Please confirm you'd like to do this.`,
            yesBtn: 'Confirm',
            confirm: function() {
              // confirmed
              resolve(true);
            }
          }
        }));
      });
    }
    else {
      return Promise.resolve(true);
    }
  }
}

export interface PartnerMapOptions {
  listSelector: boolean,
  staticFieldSelector: boolean,
  siftrockFieldSelector: boolean,
  senderFieldSelector: boolean,
  skillType: string
}



// WEBPACK FOOTER //
// ./client/app/global/partner-map/partner-map.component.ts